import React, { createContext, useContext, useState, useCallback, useEffect } from 'react'
import client from '../api/client'
import i18n, { LANG_STORAGE_KEY } from '../i18n/i18n'
import { defaultLanguageForUser } from '../i18n/pilotLanguages'

const AuthContext = createContext(null)

/**
 * Applies the i18n language for a freshly-authenticated user:
 *  - If the user has already chosen a language (saved in localStorage),
 *    keep it (respect their preference across sessions).
 *  - Otherwise, set to the role/country default (admin → en, country
 *    manager → their country, viewer → en).
 */
function applyDefaultLanguageForUser(user) {
  if (!user) return
  let saved = null
  try {
    saved = localStorage.getItem(LANG_STORAGE_KEY)
  } catch {
    // localStorage unavailable — proceed with role default.
  }
  if (saved) {
    // Detector already picked this up on init; ensure it's applied.
    if (i18n.resolvedLanguage !== saved) {
      i18n.changeLanguage(saved)
    }
    return
  }
  const next = defaultLanguageForUser(user)
  i18n.changeLanguage(next)
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [token, setToken] = useState(() => localStorage.getItem('ecoempower_token'))
  const [loading, setLoading] = useState(true)

  // Set/clear the Authorization header whenever token changes
  useEffect(() => {
    if (token) {
      client.defaults.headers.common['Authorization'] = `Bearer ${token}`
      localStorage.setItem('ecoempower_token', token)
    } else {
      delete client.defaults.headers.common['Authorization']
      localStorage.removeItem('ecoempower_token')
    }
  }, [token])

  // On mount, if we have a stored token, validate it
  // Note: the Authorization header is already set by client.js on module load
  useEffect(() => {
    const savedToken = localStorage.getItem('ecoempower_token')
    if (savedToken) {
      // Ensure header is set (belt & suspenders — client.js also does this)
      client.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`
      client.get('/api/auth/me')
        .then(data => {
          setUser(data)
          applyDefaultLanguageForUser(data)
          setLoading(false)
        })
        .catch(() => {
          // Token invalid/expired — clear everything
          setToken(null)
          setUser(null)
          localStorage.removeItem('ecoempower_token')
          delete client.defaults.headers.common['Authorization']
          setLoading(false)
        })
    } else {
      setLoading(false)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const login = useCallback(async (username, password) => {
    const data = await client.post('/api/auth/login', { username, password })
    // Set header synchronously BEFORE state updates trigger re-renders
    client.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`
    localStorage.setItem('ecoempower_token', data.access_token)
    setToken(data.access_token)
    setUser(data.user)
    applyDefaultLanguageForUser(data.user)
    return data.user
  }, [])

  const logout = useCallback(() => {
    setToken(null)
    setUser(null)
    try {
      localStorage.removeItem(LANG_STORAGE_KEY)
    } catch {
      // localStorage unavailable; nothing to clear.
    }
    i18n.changeLanguage('en')
  }, [])

  const isAdmin = user?.role === 'admin'
  const isCountryManager = user?.role === 'country_manager'
  const isViewer = user?.role === 'viewer'
  const canEdit = !!user && user.role !== 'viewer'
  const seesAllCountries = isAdmin || isViewer

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, isAdmin, isCountryManager, isViewer, canEdit, seesAllCountries }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
